Devuelve la lista paginada de llamadas de la organización autenticada por la credencial.

## Paginación

- `page` y `page_size` siguen el mismo contrato usado por la lista pública de campañas.
- La respuesta usa `count`, `next`, `previous` y `results`.
- `page` es 1-based.
- `page_size` tiene default `25` y máximo `100`.

## Filtros y ordenación

- `status`: uno o más estados de llamada.
- `direction`: uno o más sentidos de llamada.
- `origin`: uno o más orígenes.
- `call_type`: uno o más tipos canónicos de llamada.
- `campaign_id`: filtra llamadas de una campaña específica.
- `campaign_contact_id`: filtra llamadas de un contacto específico de campaña.
- `public_api_call_contact_id`: filtra llamadas de un job de contacto creado por API pública.
- `public_api_call_contact_phone_id`: filtra llamadas de un teléfono dentro del job público.
- `public_api_call_contact_status`: uno o más estados del job padre público.
- `public_api_call_contact_phone_status`: uno o más estados del teléfono dentro del job público.
- `outcome`: `converted`, `mismatch`, `optout` o `ivr_heard_to_end`.
- `is_converted`, `is_optout`, `is_mismatch`: filtros booleanos.
- `has_transfer`, `has_human_answer`, `has_ai_answer`, `has_ai_refund`: filtros operativos booleanos.
- `ivr_selected_digit`: uno o más dígitos DTMF seleccionados.
- `participant_user`: uno o más IDs de usuarios involucrados en atención humana.
- `region`, `state_code`, `ddd`, `phone_type`: filtros del número de destino.
- `start_date`, `end_date`: filtros del período de actividad.
- `ordering`: acepta los campos públicos documentados para ordenación.
- `search`: búsqueda textual en números, agente, campaña, contacto e IDs públicos relacionados.

<ApiCodeExamples exampleId="callsList" />

<ApiResponseExamples exampleId="callsList" />

## Campos principales por ítem

- `id`: ID de la llamada.
- `canonical_url`: enlace canónico de la llamada en la app Contact-Tel.
- `status`, `direction`, `origin`, `call_type`: clasificación operativa.
- `origin` puede ser `campaign`, `manual` o `public_api`.
- `to_number`, `from_number`: números involucrados en la llamada.
- `public_api_call_contact_id`, `public_api_call_contact_status`: correlación del job padre público.
- `client_reference`: referencia opaca del cliente almacenada en el job padre, o `null` para llamadas no creadas por la API pública.
- `retry_policy`: snapshot inmutable de la política del job padre, o `null` para llamadas del flujo anterior/no públicas.
- `attempts`: progreso de marcación del job padre con `count`, `remaining` y `next_attempt_at`.
- `public_api_call_contact_phone_id`, `public_api_call_contact_phone_status`, `public_api_call_contact_phone_position`: correlación del teléfono/intento dentro del job público.
- `duration`: duración operativa de la llamada.
- `ai_billed_seconds`, `ai_gross_billed_seconds`, `ai_refunded_seconds`: consumo de IA líquido, bruto y estornado.
- `telephony_billed_seconds`, `telephony_gross_billed_seconds`, `telephony_refunded_seconds`: consumo de telefonía líquido, bruto y estornado.
- `is_converted`, `is_optout`, `is_mismatch`: resultados principales expuestos para integraciones.
- `created_by_id`, `created_by_name`, `created_by_extension`: referencia del usuario creador/originador.
- `transfer_target_user_*`, `transfer_target_group_*`, `interaction_claimed_by_*`, `interaction_answered_by_*`: referencias de transferencia humana y participantes.
- `telephony_transfer_*`: línea de tiempo del handoff humano.

## Referencia de opciones

- `direction`: `outbound` significa llamada originada por la plataforma; `inbound` significa llamada recibida.
- `origin`: `campaign` fue creada por campaña; `manual` fue creada por usuario/operador; `public_api` fue creada por integración vía API pública.
- `call_type`: `ai_agent` usa agente de IA; `reverse_ivr` usa audio y DTMF; `sip` es una llamada SIP.
- `outcome`: filtro de resultado semántico; `ivr_heard_to_end` aplica a reproducción Reverse IVR.
- `to_number_phone_type`: `unknown`, `mobile`, `landline` o `sip`.
- `status`: `pending`, `queued`, `dialing`, `analyzing`, `ringing`, `in_progress`, `finalizing`, `waiting_capacity`, `waiting_operating_hours`, `completed`, `no_interaction`, `failed`, `no_answer`, `no_answer_timeout`, `busy`, `cancelled`, `transfer_unanswered`, `congestion`, `voicemail`, `invalid_number`, `config_error`, `declined`, `unreachable`, `timeout`, `mismatch`, `api_timeout`, `terminated_manual`, `capacity_timeout`, `operating_window_timeout`.

## Cuándo usar este endpoint

- seguir el historial de llamadas por campaña;
- alimentar monitoreo externo;
- sincronizar resultados operativos de llamadas en sistemas del cliente.
